import { QueryClient } from '@tanstack/react-query'
import { AppError } from '@/services/api/errors'

const statusOf = (error: unknown) => (error instanceof AppError ? error.status : undefined)

/**
 * One retry for network / server hiccups only. A 4xx answer (bad request,
 * no permission, not found…) is final — retrying it just doubles the calls.
 * 401 is already refreshed + replayed by the API client. A 429 waits a few
 * seconds before its single retry instead of hitting the limit again at once.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = statusOf(error)
  if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) return false
  return failureCount < 1
}

export function queryRetryDelay(attempt: number, error: unknown): number {
  if (statusOf(error) === 429) return 5_000
  return Math.min(1_000 * 2 ** attempt, 30_000)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})
